import { Text } from "@carbon6/c6-frontend-design";
import { type ReactNode } from "react";
import type { TrainingValidationFinding, TrainingValidationReport } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import type { LoadState } from "@/components/dataset";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import {
  hasTrainingValidationMessage,
  trainingValidationMessage,
} from "./trainingValidationMessage";
import styles from "./submitWizard.module.css";

type ValidationReportPanelProps = {
  state: LoadState<TrainingValidationReport>;
};

// 확정 스텝의 config 경고. finding 은 모두 경고라 제출을 막지 않고 참고용으로만 표시한다.
// 검사 결과가 도착하기 전에만 제출이 잠긴다(useSubmitWizardController `canSubmit`).
export function ValidationReportPanel({ state }: ValidationReportPanelProps): ReactNode {
  if (state.status === "idle") {
    return null;
  }
  return (
    <section className={styles.validationBlock} aria-label="config 경고">
      <Text variant={hatcheryTextModes["caption-bold"]}>config 경고</Text>
      {renderBody(state)}
    </section>
  );
}

function renderBody(state: LoadState<TrainingValidationReport>): ReactNode {
  if (state.status === "loading") {
    return (
      <LoadingState size="sm">
        제출 전 경고를 확인하는 중입니다. 확인이 끝나면 제출할 수 있습니다.
      </LoadingState>
    );
  }
  if (state.status === "error") {
    // 경고 검사 실패는 제출과 관계없다. 서버가 제출할 때 같은 검사를 로그로 남긴다. 제출을 막지
    // 않는 실패라서 오류 tone 을 쓰지 않는다(docs/rules.md §19).
    return (
      <EmptyState size="sm">
        경고를 불러오지 못했습니다({state.message}). 제출에는 영향이 없습니다.
      </EmptyState>
    );
  }
  if (state.status !== "success") {
    return null;
  }
  const findings = state.data.findings;
  return (
    <>
      {state.data.config_available ? null : (
        <p className={styles.validationPartial}>
          config 본문을 받지 못해 override 키·cross-key 검사를 건너뛴 부분 결과입니다. 문제가 없다는
          뜻이 아닙니다.
        </p>
      )}
      {findings.length === 0 ? (
        <EmptyState size="sm">
          {state.data.config_available
            ? "경고를 찾지 못했습니다."
            : "건너뛴 축 밖에서는 경고가 없습니다."}
        </EmptyState>
      ) : (
        <>
          <p className={styles.hint}>경고는 참고용이며 제출을 막지 않습니다.</p>
          <ul className={styles.validationList}>
            {findings.map((finding, index) => (
              <ValidationFindingRow
                key={`${finding.code}:${finding.config_key ?? ""}:${index}`}
                finding={finding}
              />
            ))}
          </ul>
        </>
      )}
    </>
  );
}

function ValidationFindingRow({ finding }: { finding: TrainingValidationFinding }): ReactNode {
  return (
    <li className={styles.validationRow}>
      <span className={styles.validationBadgeWarning}>경고</span>
      <div className={styles.validationText}>
        <p className={styles.validationMessage}>{trainingValidationMessage(finding)}</p>
        {/* 아는 코드는 한국어 문장이 원문을 대신한다. 모르는 코드는 원문이 곧 문장이라 원문을 또 표시하면 중복이다. */}
        {hasTrainingValidationMessage(finding.code) ? (
          <p className={styles.validationDetail}>{finding.message}</p>
        ) : null}
        <p className={styles.validationMeta}>
          <code>{finding.code}</code>
          {finding.config_key === null ? null : <code>{finding.config_key}</code>}
        </p>
      </div>
    </li>
  );
}
